import React, { useState, useEffect } from 'react';
import { RightResp, useAdminApi } from '../../api/adminApi';
import {
    Box, Button, Chip, List, ListItem,
    ListItemText, MenuItem, Select, Typography,
    Snackbar, Alert, IconButton, AlertColor,
    Grid, Card, CardHeader, CardContent,
    Avatar, Tooltip, Divider
} from '@mui/material';
import { Delete, DragIndicator, Lock, LockOpen, Group } from '@mui/icons-material';
import ConfirmationDialog from '../../common/ConfirmationDialog';

export const RoleRightsPage = () => {
    const {
        getAllRoles,
        getAllRights,
        getRightsByRole,
        assignRightToRole,
        revokeRightFromRole
    } = useAdminApi();

    const [roles, setRoles] = useState<string[]>([]);
    const [rights, setRights] = useState<RightResp[]>([]);
    const [selectedRole, setSelectedRole] = useState('');
    const [roleRights, setRoleRights] = useState<string[]>([]);
    const [newRightName, setNewRightName] = useState('');
    const [confirmRevokeOpen, setConfirmRevokeOpen] = useState(false);
    const [rightToRevoke, setRightToRevoke] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{
        open: boolean;
        message: string;
        severity: AlertColor;
    }>({ open: false, message: '', severity: 'success' });
    const [draggedItem, setDraggedItem] = useState<string | null>(null);

    const loadData = async () => {
        const [rolesRes, rightsRes] = await Promise.all([
            getAllRoles(),
            getAllRights()
        ]);
        if (rolesRes.data?.data) setRoles(rolesRes.data.data);
        if (rightsRes.data?.data) setRights(rightsRes.data.data);
    };

    const loadRoleRights = async () => {
        if (!selectedRole) return;
        const res = await getRightsByRole(selectedRole);
        if (res.data?.data) setRoleRights(res.data.data);
    };

    useEffect(() => { loadData(); }, []);
    useEffect(() => { loadRoleRights(); }, [selectedRole]);

    const handleAssignRight = async () => {
        if (!newRightName) return;
        try {
            await assignRightToRole(selectedRole, newRightName);
            setSnackbar({ open: true, message: 'Yetki başarıyla atandı', severity: 'success' });
            loadRoleRights();
            setNewRightName('');
        } catch (error) {
            setSnackbar({ open: true, message: 'Yetki atama başarısız', severity: 'error' });
        }
    };

    const handleRevokeRight = async () => {
        if (!rightToRevoke || !selectedRole) return;

        try {
            await revokeRightFromRole(selectedRole, rightToRevoke);
            setSnackbar({ open: true, message: 'Yetki başarıyla kaldırıldı', severity: 'success' });
            loadRoleRights();
        } catch (error) {
            setSnackbar({ open: true, message: 'Yetki kaldırma başarısız', severity: 'error' });
        } finally {
            setConfirmRevokeOpen(false);
            setRightToRevoke(null);
        }
    };

    const handleDragStart = (e: React.DragEvent<HTMLLIElement>, right: string) => {
        setDraggedItem(right);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', right);
    };

    const handleDragOver = (e: React.DragEvent<HTMLLIElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
    };

    const handleDrop = (e: React.DragEvent<HTMLLIElement>, targetRight: string) => {
        e.preventDefault();
        if (!draggedItem || draggedItem === targetRight || !selectedRole) return;

        const newRights = [...roleRights];
        const draggedIndex = newRights.indexOf(draggedItem);
        const targetIndex = newRights.indexOf(targetRight);

        newRights.splice(draggedIndex, 1);
        newRights.splice(targetIndex, 0, draggedItem);

        setRoleRights(newRights);
        setDraggedItem(null);
    };

    return (
        <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
            <Typography variant="h4" gutterBottom sx={{
                mb: 3,
                fontWeight: 'bold',
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                gap: 1
            }}>
                <Group fontSize="large" /> Rol Yetki Yönetimi
            </Typography>

            <Card sx={{ mb: 3, boxShadow: 3 }}>
                <CardHeader
                    title="Rol Seçimi"
                    avatar={
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                            <Lock />
                        </Avatar>
                    }
                />
                <CardContent>
                    <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                        <Select
                            value={selectedRole}
                            onChange={(e) => setSelectedRole(e.target.value as string)}
                            displayEmpty
                            sx={{ minWidth: 250 }}
                            size="small"
                        >
                            <MenuItem value="">Bir rol seçiniz...</MenuItem>
                            {roles.map((role) => (
                                <MenuItem key={role} value={role}>{role}</MenuItem>
                            ))}
                        </Select>

                        {selectedRole && (
                            <>
                                <Select
                                    value={newRightName}
                                    onChange={(e) => setNewRightName(e.target.value as string)}
                                    displayEmpty
                                    sx={{ minWidth: 250 }}
                                    size="small"
                                >
                                    <MenuItem value="">Atanabilir yetki seçiniz...</MenuItem>
                                    {rights
                                        .filter(r => !roleRights.includes(r.name))
                                        .map((right) => (
                                            <MenuItem key={right.name} value={right.name}>
                                                {right.name}
                                            </MenuItem>
                                        ))}
                                </Select>
                                <Button
                                    variant="contained"
                                    onClick={handleAssignRight}
                                    disabled={!newRightName}
                                    size="medium"
                                >
                                    Yetki Ata
                                </Button>
                            </>
                        )}
                    </Box>
                </CardContent>
            </Card>

            {selectedRole && (
                <Grid container spacing={3}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', boxShadow: 3 }}>
                            <CardHeader
                                title={`${selectedRole} Rolünün Yetkileri`}
                                subheader={`Toplam ${roleRights.length} yetki`}
                                avatar={
                                    <Avatar sx={{ bgcolor: 'success.main' }}>
                                        <LockOpen />
                                    </Avatar>
                                }
                                action={
                                    <Tooltip title="Sürükle bırak ile sıralayabilirsiniz">
                                        <DragIndicator color="action" />
                                    </Tooltip>
                                }
                            />
                            <Divider />
                            <CardContent sx={{ p: 0 }}>
                                <List sx={{
                                    maxHeight: 500,
                                    overflow: 'auto',
                                    '& .MuiListItem-root': {
                                        transition: 'all 0.2s ease'
                                    }
                                }}>
                                    {roleRights.map((right) => (
                                        <ListItem
                                            key={right}
                                            draggable
                                            onDragStart={(e) => handleDragStart(e, right)}
                                            onDragOver={handleDragOver}
                                            onDrop={(e) => handleDrop(e, right)}
                                            sx={{
                                                cursor: 'move',
                                                '&:hover': {
                                                    backgroundColor: 'action.hover',
                                                    transform: 'scale(1.01)'
                                                },
                                                borderBottom: '1px solid',
                                                borderColor: 'divider',
                                                py: 1.5
                                            }}
                                            secondaryAction={
                                                <Tooltip title="Yetkiyi kaldır">
                                                    <IconButton
                                                        onClick={() => {
                                                            setRightToRevoke(right);
                                                            setConfirmRevokeOpen(true);
                                                        }}
                                                        color="error"
                                                        edge="end"
                                                    >
                                                        <Delete />
                                                    </IconButton>
                                                </Tooltip>
                                            }
                                        >
                                            <Avatar sx={{
                                                bgcolor: 'secondary.main',
                                                width: 32,
                                                height: 32,
                                                mr: 2
                                            }}>
                                                <DragIndicator fontSize="small" />
                                            </Avatar>
                                            <ListItemText
                                                primary={right}
                                                primaryTypographyProps={{ fontWeight: 'medium' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid size={{ xs: 12, md: 6 }}>
                        <Card sx={{ height: '100%', boxShadow: 3 }}>
                            <CardHeader
                                title="Atanabilir Yetkiler"
                                subheader={`${rights.filter(r => !roleRights.includes(r.name)).length} yetki mevcut`}
                                avatar={
                                    <Avatar sx={{ bgcolor: 'info.main' }}>
                                        <Lock />
                                    </Avatar>
                                }
                            />
                            <Divider />
                            <CardContent sx={{
                                maxHeight: 500,
                                overflow: 'auto',
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: 1,
                                p: 2
                            }}>
                                {rights
                                    .filter(r => !roleRights.includes(r.name))
                                    .map((right) => (
                                        <Chip
                                            key={right.name}
                                            label={right.name}
                                            onClick={() => setNewRightName(right.name)}
                                            sx={{
                                                m: 0.5,
                                                cursor: 'pointer',
                                                '&:hover': {
                                                    transform: 'scale(1.05)'
                                                }
                                            }}
                                            color={newRightName === right.name ? 'primary' : 'default'}
                                            variant={newRightName === right.name ? 'filled' : 'outlined'}
                                            clickable
                                        />
                                    ))}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            )}

            <ConfirmationDialog
                open={confirmRevokeOpen}
                title="Yetki Kaldırma Onayı"
                message={`"${rightToRevoke}" yetkisini ${selectedRole} rolünden kaldırmak istediğinize emin misiniz?`}
                onConfirm={handleRevokeRight}
                onClose={() => setConfirmRevokeOpen(false)}
            />

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
            >
                <Alert severity={snackbar.severity}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};